/**
 * World 10 — "First Shore" (All). Narrative fragment for campaignStories.ts.
 * THE GRAND FINALE (Act II, chapter 5, levels 91–100).
 *
 * Recast 2026-07-10 to Pokémon Red trainer-dialogue complexity (short, simple
 * lines): docs/superpowers/specs/2026-07-10-campaign-pokered-dialogue-rework.md.
 *
 * THE TALE: the chase ends where the island began — First Shore, older than
 * names, where the first Keeper bonded the first Guardian. The whole cast fights
 * the approach at your side: Timmy and Chirpy at your flank, the twins Mimi &
 * Lulu in stereo, Vera and Cliff holding the line, Aggie telling the true
 * story out loud as a weapon. First Guard hands over the First Keeper's own EMPTY
 * book. The Soul-Eater's last plea is a battle against his hunger, not the man.
 * First Champ yields the fourth First-Soul shard; the First Friend (Pikoto)
 * yields the eighteenth; Firstheart (Trixon) — the true final battle — yields
 * only when the strand is passed hand to hand down the line of friends. Nineteen shards fly home to
 * the First Hearth, and the Soul still flickers: it is missing the one the
 * island forgot. The player says his name; everyone joins; the Soul shines
 * whole and the island is back in balance. In the ending, by the same fire as
 * the opening, the cast finally speaks the player's own name.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD10_TAGLINE =
  "Gray has stopped running. He waits at the First Hearth, where the island's Soul once hung. It has been cold for a thousand years. Bring everyone, and bring all nineteen home.";

export const WORLD10_ZONE_STORIES: Record<string, string> = {
  z27: "The whole group walks the first beach. Aggie tells Gray's story as it really happened. Nobody interrupts, not even Timmy. Far down the sand, Clan Z's cage cart rolls onto the beach. Zeck still wants the last shards.",
  z28: "Pictures rise in the foam: young Gray, a Guardian, a warm fire. The twins call to each other across the water while Cliff watches the path behind you.",
  z29: "Gray sits beside the First Hearth. Vera and Cliff hold the path. Take your team through. The others will follow.",
};

export const WORLD10_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 27: Origin Sands (1v1) ---
  "z27-n1": {
    story: "On the sand, an echo of the island's first days waits for you.",
    dialogue: {
      intro: "Before there were Keepers, there was one person and one Suikamon. Show me what that first bond became.",
      playerWin: "Good. Gray is waiting. Stay together when you find him.",
      playerLose: "Another try. There's still time to get this right.",
    },
  },
  "z27-n2": {
    story: "Zip and Zap ambush you on the first beach. Clan Z wants the last shards.",
    dialogue: {
      intro: "Surprise! Zeck says the last shards are ours! Dash just rolled over and flattened a dune! Get 'em!",
      playerIntro: "Not a chance, Zip.",
      playerWin: "Retreat! Again! Zap, why do we always lose?!",
      playerLose: "Ha! Clan Z wins the first beach!",
      introRematchAfterLoss: "Back for more? Dash is warmed up! Shade too.",
      introRematchAfterWin: "Rematch! Zap counted. We're only a little behind! Like, a hundred.",
      playerWinRepeat: "Fine! Keep the silly beach! Zeck is going to be so mad.",
      playerLoseRepeat: "We won one! Zap, write that down! ...He won't.",
    },
  },
  "z27-n3": {
    story: "A guard holds out a book to you. Nothing is written inside.",
    dialogue: {
      intro: "I kept Gray's old book. It's been empty all these years. Show me I can trust you with it.",
      playerWin: "Here. His old book, not another shard. Keep the first page empty for his name.",
      playerLose: "I'll keep it safe a little longer. Come back when you're ready.",
    },
  },
  "z27-n4": {
    story: "A great gate stands in front of you, sand piled high against its shut doors.",
    dialogue: {
      intro: "Creeeak. Shut for a thousand years. Knock with your team, Keeper, and see if I open.",
      playerWin: "The gate swings open. Sand pours from hinges that have not moved in a thousand years.",
      playerLose: "The gate holds. Your team needs another try.",
    },
  },

  // --- Zone 28: Year Tides (2v2) ---
  "z28-n1": {
    story: "A memory rises around you: a Guardian resting its head in human hands.",
    dialogue: {
      intro: "Long ago, Gray held out his hands. The Guardian trusted him. Remember that. Now show me in a battle.",
      playerWin: "The memory stays clear. You can show it to him when you reach him.",
      playerLose: "The memory slips beneath the tide. Regroup and reach for it again.",
    },
  },
  "z28-n2": {
    story: "In the tide, a lonely cat sits on a little rock. It keeps a space beside it, just for you.",
    dialogue: {
      intro: "Everyone leaves eventually. Stay here. You won't have to wait for anyone again.",
      playerWin: "You step through the water. Behind you, your friends follow. Nobody is left waiting.",
      playerLose: "The tide tugs at your feet. Your friends call you back to shore.",
    },
  },
  "z28-n3": {
    story: "In the shallows, a worried Trixon tilts its head at you. It reminds you of Chirpy's lost name, and of Lulu. Do you still want to save Gray?",
    dialogue: {
      intro: "What if he doesn't believe you? What if you find him and it changes nothing?",
      playerWin: "You keep going. You can't promise he'll believe you. But you can be there.",
      playerLose: "You hesitate. The tide catches you. Rest with your friends, then try again.",
    },
  },
  "z28-n4": {
    story: "A giant gray wave rises over you, cold as a hearth nobody has lit for a thousand years.",
    dialogue: {
      intro: "A thousand years of waiting. Do you really think you can cross them?",
      playerWin: "The wave breaks. The cold hearth is close now.",
      playerLose: "The wave carries you back to the shallows. Your friends help you stand.",
    },
  },

  // --- Zone 29: First Hearth (3v3) ---
  "z29-n1": {
    story: "First Champ, the old Sumiya from Champion Road, walks up to you. A warm golden glow sits in its paws.",
    dialogue: {
      intro: "You helped me on Champion Road. One more match, Keeper. Then we go to him together.",
      playerWin: "Not for your Shard Book. A memory of who Gray was. Carry it to him.",
      playerLose: "Not quite. Rest your team. I'm coming with you when you're ready.",
    },
  },
  "z29-n2": {
    story: "The Soul-Eater speaks in a tired, familiar voice. It offers you rest.",
    dialogue: {
      intro: "The wrapper. The pebble. The feather. Put them down. You must be so tired.",
      playerWin: "You hold on to the gifts. The hungry voice goes quiet. Gray is still waiting.",
      playerLose: "The gifts slip in your hands. You gather them again and step back to rest.",
    },
  },
  "z29-n3": {
    story: "A little Pikoto stands between you and the cold Hearth. It has guarded it for a thousand years.",
    dialogue: {
      intro: "Peep! Peep! The little Pikoto puffs up as big as it can. Nobody gets past it to the Hearth.",
      playerWin: "The Pikoto hops into your palm and offers its shard. Eighteen! Timmy crouches beside it. 'A thousand years? That's like... a hundred birthdays!' Chirpy flexes back at it.",
      playerLose: "The Pikoto stands firm. Small doesn't mean easy. Give your team a rest.",
    },
  },
  "z29-n4": {
    story: "Gray kneels by the cold Hearth. Firstheart, the great Trixon, rises over him. Its wave sends the last shard rolling past you, and Timmy slips toward the water.",
    dialogue: {
      intro: "Firstheart has kept Gray safe since the very first day. It doesn't know you yet. Show it why you came.",
      playerWin: "You let the shard roll and grab Timmy. Feet planted, as Cliff taught you. Your friends pull you both back. Firstheart sees, and its shard rises into your book. Nineteen!",
      playerLose: "Firstheart drives you back. Your friends gather close. Gray is still there. Try again together.",
    },
  },
};
